// Claiming a moved profile at the counter (F-IAM-13, spec/13 §8.2): a centred dialog over the "Moved" list.
//
// Staff check who the person is, then move what waits for them to a Clavian account: one of the organisation's
// players, found by name or phone, or an email address the player proves with the code sent to it (a new account is
// made only when they accept the terms themselves). An under-18's profile needs a parent or guardian present, by name.
// Above the café's limit a manager approves with their staff PIN: the dialog asks for it when the server says so.

import type { ImportedPlayer, OrganizationPlayer } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, Dialog, RadioGroup, SelectField, TextField } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { fetchBrandLegal } from '../../api/meta';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { queryKeys } from '../../api/queries';
import { ConsentLabel } from '../../auth/consent';
import { useScopeIds } from '../../shell/scope';
import { PlayerPicker } from '../sessions/PlayerPicker';
import { useClaimApprovers, useCounterClaim, type CounterClaimBody } from './queries';

/** A moved player's name as staff call them: their name, or their old username. */
function movedName(player: ImportedPlayer): string {
  const name = [player.firstName, player.lastName].filter((part): part is string => part !== null && part !== '').join(' ');
  return name === '' ? player.username : name;
}

/** The two generic codes this dialog can say more about than the catalogue does; every other code reads from the catalogue. */
const CONTEXT_KEYS: Readonly<Record<string, string>> = {
  [PROBLEM.conflict]: 'players.moved.claim.error.taken',
  [PROBLEM.notFound]: 'players.moved.claim.error.player',
};

export function CounterClaimDialog({
  player,
  platform,
  onClose,
}: {
  readonly player: ImportedPlayer | null;
  readonly platform: string;
  readonly onClose: () => void;
}): ReactNode {
  return player === null ? null : <ClaimForm key={player.id} player={player} platform={platform} onClose={onClose} />;
}

function ClaimForm({ player, platform, onClose }: { readonly player: ImportedPlayer; readonly platform: string; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const api = useApi();
  const problem = useProblem();
  const scope = useScopeIds();
  const { sendCode, claim } = useCounterClaim(scope.organizationId, player.id);
  const legal = useQuery({ queryKey: queryKeys.meta, queryFn: () => fetchBrandLegal(api), staleTime: 300_000 });

  const [checked, setChecked] = useState(false);
  const [guardianName, setGuardianName] = useState('');
  const [guardianHere, setGuardianHere] = useState(false);
  const [to, setTo] = useState<'player' | 'email'>(player.email === null ? 'email' : 'player');
  const [target, setTarget] = useState<OrganizationPlayer | null>(null);
  const [email, setEmail] = useState(player.email !== null && !player.email.includes('•') ? player.email : '');
  const [code, setCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [approver, setApprover] = useState('');
  const [pin, setPin] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const approvers = useClaimApprovers(scope.organizationId, player.id, needsApproval);
  const day = (date: string) => format.date(`${date}T12:00:00.000Z`, 'medium');

  const done = claim.data;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready =
    checked &&
    (!player.minor || (guardianHere && guardianName.trim().length >= 2)) &&
    (to === 'player' ? target !== null : emailValid && /^\d{6}$/.test(code)) &&
    (!needsApproval || (approver !== '' && /^\d{6}$/.test(pin)));

  const submit = () => {
    setFailure(null);
    const body: CounterClaimBody = {
      identityChecked: true,
      ...(to === 'player' && target !== null ? { playerId: target.id } : {}),
      ...(to === 'email'
        ? {
            email: email.trim(),
            code,
            ...(consent && legal.data !== undefined ? { consent: { terms: legal.data.terms.version, privacy: legal.data.privacy.version } } : {}),
          }
        : {}),
      ...(player.minor ? { guardian: { present: guardianHere, name: guardianName.trim() } } : {}),
      ...(needsApproval ? { approval: { memberId: approver, pin } } : {}),
    } as CounterClaimBody;
    claim.mutate(body, {
      onError: (error) => {
        const code = problemCode(error);
        if (code === 'carried_over_approval_required') {
          setNeedsApproval(true);
          return;
        }
        setPin('');
        const contextKey = CONTEXT_KEYS[code ?? ''];
        setFailure(contextKey !== undefined ? t(contextKey) : problem.text(error, { fallback: t('players.moved.claim.error.generic') }));
      },
    });
  };

  const money = format.money(player.cash);
  const time = player.timeSeconds > 0 ? format.duration(player.timeSeconds) : t('players.moved.claim.none');

  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="lg"
      title={t('players.moved.claim.title')}
      description={t('players.moved.claim.description', { player: movedName(player), platform })}
      dirty={done === undefined && (checked || code !== '' || target !== null)}
      footer={
        done !== undefined ? (
          <Button variant="primary" onClick={onClose}>{t('players.moved.claim.close')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t('players.moved.claim.cancel')}
            </Button>
            <Button variant="primary" busy={claim.isPending} disabled={!ready} onClick={submit}>
              {t('players.moved.claim.submit')}
            </Button>
          </>
        )
      }
    >
      <div className="console-claim">
        <section className="console-claim__summary" aria-label={t('players.moved.claim.waiting')}>
          <div className="console-claim__who">
            <strong>{movedName(player)}</strong>
            <span className="console-subtle">@{player.username}</span>
            <span className="console-people-tags">
              {player.minor ? <Badge tone="warn">{t('players.moved.minor')}</Badge> : null}
              {player.banned ? <Badge tone="danger">{t('players.moved.banned')}</Badge> : null}
            </span>
          </div>
          <dl className="console-claim__facts">
            <div>
              <dt>{t('players.moved.claim.money')}</dt>
              <dd className="console-money">
                {money}
                {player.cashTaxPaid.amount > 0 ? <Badge tone="info">{t('players.moved.gstPaid')}</Badge> : null}
              </dd>
            </div>
            <div>
              <dt>{t('players.moved.claim.time')}</dt>
              <dd className="console-money">
                {time}
                {player.timeSeconds > 0 && player.timeValidUntil !== null ? <span className="console-subtle"> {t('players.moved.claim.timeUntil', { date: day(player.timeValidUntil) })}</span> : null}
              </dd>
            </div>
          </dl>
        </section>

        {done !== undefined ? (
          <div className="console-claim__done" role="status">
            <p>
              {done.carriedOver.timeSeconds > 0
                ? t('players.moved.claim.done', { money: format.money(done.carriedOver.cash), time: format.duration(done.carriedOver.timeSeconds) })
                : t('players.moved.claim.doneMoney', { money: format.money(done.carriedOver.cash) })}
            </p>
            {done.createdAccount ? <p className="console-subtle">{t('players.moved.claim.doneNew', { email: email.trim() })}</p> : null}
          </div>
        ) : (
          <>
            {player.banned ? <p className="console-claim__note">{t('players.moved.claim.bannedNote')}</p> : null}
            <Checkbox label={t('players.moved.claim.check')} checked={checked} onCheckedChange={(value) => setChecked(value === true)} />

            {player.minor ? (
              <fieldset className="console-claim__group">
                <p className="console-claim__note">{t('players.moved.claim.minorNote')}</p>
                <TextField label={t('players.moved.claim.guardianName')} value={guardianName} onChange={(event) => setGuardianName(event.target.value)} autoComplete="off" />
                <Checkbox label={t('players.moved.claim.guardianPresent')} checked={guardianHere} onCheckedChange={(value) => setGuardianHere(value === true)} />
              </fieldset>
            ) : null}

            <RadioGroup
              label={t('players.moved.claim.to')}
              orientation="horizontal"
              value={to}
              onValueChange={(value) => setTo(value === 'email' ? 'email' : 'player')}
              options={[
                { value: 'player', label: t('players.moved.claim.toPlayer') },
                { value: 'email', label: t('players.moved.claim.toEmail') },
              ]}
            />
            {to === 'player' ? (
              <PlayerPicker organizationId={scope.organizationId} player={target} onPick={setTarget} label={t('players.moved.claim.playerSearch')} />
            ) : (
              <div className="console-claim__group">
                <div className="console-claim__row">
                  <TextField
                    label={t('players.moved.claim.email')}
                    description={t('players.moved.claim.emailHint')}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="off"
                  />
                  <Button variant="secondary" busy={sendCode.isPending} disabled={!emailValid} onClick={() => sendCode.mutate(email.trim())}>
                    {sendCode.isSuccess ? t('players.moved.claim.resendCode') : t('players.moved.claim.sendCode')}
                  </Button>
                </div>
                {sendCode.isSuccess ? (
                  <p className="console-subtle" role="status">
                    {t('players.moved.claim.codeSent', { email: email.trim() })}
                  </p>
                ) : null}
                <TextField label={t('players.moved.claim.code')} inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} autoComplete="one-time-code" />
                <Checkbox label={<ConsentLabel legal={legal.data} />} checked={consent} onCheckedChange={(value) => setConsent(value === true)} />
                <p className="console-subtle">{t('players.moved.claim.consentHint')}</p>
              </div>
            )}

            {needsApproval ? (
              <fieldset className="console-claim__group console-claim__approval">
                <legend>{t('players.moved.claim.approvalTitle')}</legend>
                <p className="console-subtle">{t('players.moved.claim.approvalBody')}</p>
                {approvers.data !== undefined && approvers.data.data.length === 0 ? (
                  <p className="console-claim__note">{t('players.moved.claim.noApprovers')}</p>
                ) : (
                  <div className="console-claim__row">
                    <SelectField
                      label={t('players.moved.claim.approver')}
                      placeholder={t('players.moved.claim.approverPlaceholder')}
                      value={approver}
                      onValueChange={setApprover}
                      options={(approvers.data?.data ?? []).map((one) => ({ value: one.memberId, label: one.name }))}
                    />
                    <TextField label={t('players.moved.claim.pin')} type="password" inputMode="numeric" maxLength={6} value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, ''))} autoComplete="off" />
                  </div>
                )}
              </fieldset>
            ) : null}

            {failure === null ? null : (
              <p className="console-claim__error" role="alert">
                {failure}
              </p>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
